import { User, Company, Permission, UserRole } from '../types/auth';
import type { WorkspaceMembership, WorkspaceRole } from '../types/workspace';

// Internal users bypass per-workspace filtering — the consultant
// pattern. Customer-side roles (workspace_*) are scoped to their
// own workspace. `staff` is a legacy alias treated as canonical.
// `admin` is deliberately NOT here, and matches the server's
// lib/access_control.isInternalRole: it is the role client onboarding
// assigns to a CUSTOMER's own account-holder, so admitting it handed a
// customer the cross-workspace bypass.
// Exported: AuthContext's tier getters need the same exemption — platform
// staff can HOLD workspace memberships (owning their own workspace) without
// those rows reclassifying them as workspace-tier people.
export function isInternalRole(role?: UserRole): boolean {
  return role === 'super_admin' || role === 'internal' || role === 'staff';
}

/** Platform administrator — the tier the UI means when it says "administrators".
 *  Narrower than isInternalRole, which also admits `internal` and `staff`:
 *  someone can be on the Contract Group side without being an admin.
 *
 *  `admin` is excluded for the same reason as above — it is a customer-side
 *  account-holder role, and letting it through meant a customer read the
 *  admin-only Sales sections. super_admin still passes, which is the tier
 *  every one of those gates actually means.
 *
 *  Exported so screens that receive `user` as a prop rather than reading the
 *  auth context share ONE definition with it. FinanceBilling used to inline
 *  `user.role === 'admin'`, which excluded super_admin — a strictly higher
 *  tier — and locked the owner out of the billing screen.
 */
export function isAdministratorRole(role?: UserRole): boolean {
  return role === 'super_admin';
}

/** Customer-side workspace user (any tier). Includes legacy aliases. */
export function isWorkspaceRole(role?: UserRole): boolean {
  return role === 'workspace_owner' || role === 'workspace_admin' || role === 'workspace_user'
      || role === 'client'          || role === 'member'          || role === 'supervisor';
}

/** Workspace owner — full per-workspace control. Includes legacy aliases. */
export function isWorkspaceOwnerRole(role?: UserRole): boolean {
  return role === 'workspace_owner' || role === 'client' || role === 'member';
}

/** Programme activities are project collaboration data, so every signed-in
 * project role may maintain them. Keep this predicate shared between the web
 * Planner and native header so the iPhone `+` never drifts from the editor's
 * actual access gate. Service accounts remain read-only. */
export function canEditProjectPlanner(role?: string | null): boolean {
  return role === 'super_admin' || role === 'internal'
      || role === 'workspace_owner' || role === 'workspace_admin' || role === 'workspace_user'
      || role === 'admin' || role === 'staff' || role === 'client'
      || role === 'member' || role === 'supervisor';
}

/**
 * Whether the user may edit settings for the workspace they are currently in.
 *
 * This deliberately uses the active membership rather than the account-level
 * role. A user may administer one workspace and be an ordinary member of
 * another, while platform staff use the separate cross-workspace directory.
 */
export function canManageActiveWorkspaceSettings(
  activeWorkspaceId: string | null | undefined,
  memberships: readonly WorkspaceMembership[] | null | undefined,
): boolean {
  if (!activeWorkspaceId) return false;

  return (memberships ?? []).some((membership) =>
    membership.workspaceId === activeWorkspaceId &&
    membership.status === 'active' &&
    (membership.workspaceRole === 'workspace_owner' ||
      membership.workspaceRole === 'workspace_admin'),
  );
}

export function getUserWorkspaceId(user: User | null | undefined): string | null {
  if (!user) return null;
  return (
    (user as any).workspaceId       ||
    (user as any).workspace_id      ||
    (user as any).activeWorkspaceId ||
    (user as any).active_workspace_id ||
    user.company_id                 ||
    user.companyId                  ||
    null
  );
}

/** @deprecated use getUserWorkspaceId */
function getUserCompanyId(user: User | null | undefined): string | null {
  return getUserWorkspaceId(user);
}

// Per-workspace capabilities live on workspace_memberships.permissions
// and are merged in elsewhere. Legacy keys mirror their canonical
// equivalents so in-flight references keep resolving.
const SUPER_ADMIN_PERMS: Permission[] = [
  'view_all_companies',
  'manage_users',
  'manage_system',
  'view_own_projects',
  'manage_own_projects',
  'view_financial_data',
  'export_data',
  'manage_variations',
  'approve_applications',
];

const INTERNAL_PERMS: Permission[] = [
  'view_all_companies',
  'view_own_projects',
  'manage_own_projects',
  'view_financial_data',
  'export_data',
  'manage_variations',
  'approve_applications',
];

// Customer-side workspace owner — full per-workspace control. Owner-
// vs-admin distinction (Org Profile, billing, member removal) is
// gated separately at the membership-permissions layer, not here.
const WORKSPACE_OWNER_PERMS: Permission[] = [
  'view_own_projects',
  'view_financial_data',
  'export_data',
];

// Workspace admin — same baseline as owner; owner-only powers
// gated separately at the membership level.
const WORKSPACE_ADMIN_PERMS: Permission[] = [
  'view_own_projects',
  'view_financial_data',
  'export_data',
];

// Workspace user — site-team baseline. Includes the daily-diary /
// dayworks / deliveries / site-reports perms inherited from the
// legacy supervisor role so site users keep their existing gates
// after the rename.
const WORKSPACE_USER_PERMS: Permission[] = [
  'view_own_projects',
  'view_assigned_projects',
  'submit_site_reports',
  'log_daily_diary',
  'submit_dayworks',
  'report_site_deliveries',
];

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  // Canonical
  super_admin:     SUPER_ADMIN_PERMS,
  internal:        INTERNAL_PERMS,
  workspace_owner: WORKSPACE_OWNER_PERMS,
  workspace_admin: WORKSPACE_ADMIN_PERMS,
  workspace_user:  WORKSPACE_USER_PERMS,
  bot:             [],
  // Legacy aliases (DB no longer writes these; kept for cached sessions)
  // `admin` keeps its SUPER_ADMIN_PERMS mapping even though the role
  // predicates above no longer admit it: shipped client bundles still index
  // this map by user.role, and a missing key would strip a cached session of
  // every permission mid-session. No live user holds it, so the mapping is
  // unreachable in practice — remove it once the pinned clients age out.
  admin:           SUPER_ADMIN_PERMS,
  staff:           INTERNAL_PERMS,
  client:          WORKSPACE_OWNER_PERMS,
  member:          WORKSPACE_OWNER_PERMS,
  supervisor:      WORKSPACE_USER_PERMS,
};

// Membership-first: a caller's ACTIVE workspace_memberships row (passed by
// the AuthContext, which resolves it from activeWorkspaceId) takes priority
// over the legacy global role for workspace-tier permission sets. Platform
// staff (super_admin/internal/admin/staff) are exempt — they aren't scoped
// to a single workspace tier, so they keep their existing direct mapping
// regardless of any membership row. No membership resolved (legacy accounts
// pre-dating workspace_memberships) falls back to the legacy user.role
// lookup, verbatim.
export function getUserPermissions(user: User, activeMembershipRole?: WorkspaceRole | null): Permission[] {
  if (!user || !user.role) {
    return [];
  }
  const rolePermissions =
    !isInternalRole(user.role) && activeMembershipRole && ROLE_PERMISSIONS[activeMembershipRole]
      ? ROLE_PERMISSIONS[activeMembershipRole]
      : (ROLE_PERMISSIONS[user.role] || []);
  const userPermissions = user.permissions || [];
  // Combine role permissions with any additional user-specific permissions
  return [...new Set([...rolePermissions, ...userPermissions])];
}

export function canUserAccess(user: User, permission: Permission, activeMembershipRole?: WorkspaceRole | null): boolean {
  if (!user) {
    return false;
  }
  const userPermissions = getUserPermissions(user, activeMembershipRole);
  return userPermissions.includes(permission);
}

// Accepts a workspace id OR a legacy company id — they resolve to
// the same business entity, so callers passing project.client_id
// still get the right answer.
export function canUserViewWorkspace(user: User, workspaceOrCompanyId: string): boolean {
  if (!user || !user.role || !workspaceOrCompanyId) return false;
  if (isInternalRole(user.role)) return true;
  return getUserWorkspaceId(user) === workspaceOrCompanyId;
}

/** @deprecated use canUserViewWorkspace */
export function canUserViewCompany(user: User, companyId: string): boolean {
  return canUserViewWorkspace(user, companyId);
}

// Defensive about both naming conventions: raw rows carry snake_case,
// post-transform entities carry camelCase. Same row matches under either.
export function filterDataByWorkspaceAccess<T extends {
  workspaceId?: string; workspace_id?: string;
  client_workspace_id?: string; clientWorkspaceId?: string;
  client_company_id?: string; company_id?: string; clientId?: string;
}>(
  data: T[],
  user: User,
): T[] {
  if (!user || !user.role || !Array.isArray(data)) return [];
  if (isInternalRole(user.role)) return data;

  const userWorkspaceId = getUserWorkspaceId(user);
  if (!userWorkspaceId) return [];

  return data.filter(item => {
    const itemWorkspaceId =
      item.workspaceId ?? item.workspace_id ??
      item.clientWorkspaceId ?? item.client_workspace_id ??
      item.client_company_id ?? item.company_id ?? item.clientId;
    return itemWorkspaceId === userWorkspaceId;
  });
}

/** @deprecated use filterDataByWorkspaceAccess */
export function filterDataByCompanyAccess<T extends { client_company_id?: string; company_id?: string; clientId?: string }>(
  data: T[],
  user: User,
): T[] {
  return filterDataByWorkspaceAccess(data as any, user) as T[];
}

export function getVisibleCompanies(companies: Company[], user: User): Company[] {
  if (isInternalRole(user.role)) return companies;
  const userWorkspaceId = getUserWorkspaceId(user);
  if (!userWorkspaceId) return [];
  return companies.filter(company =>
    company.id === userWorkspaceId ||
    (company as any).workspaceId === userWorkspaceId,
  );
}
